import { useEffect, useRef } from "react";
import "@/App.css";

import { LanguageProvider } from "@/lib/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import Nav from "@/components/sections/Nav";
import Hero from "@/components/sections/Hero";
import About from "@/components/sections/About";
import Services from "@/components/sections/Services";
import Tools from "@/components/sections/Tools";
import Projects from "@/components/sections/Projects";
import Marquee from "@/components/sections/Marquee";
import Pricing from "@/components/sections/Pricing";
import Faq from "@/components/sections/Faq";
import Contact from "@/components/sections/Contact";

import { initSmoothScroll, destroySmoothScroll } from "@/three/smoothScroll";
import { initScrollAnimations } from "@/three/scrollAnimations";

function App() {
    const particlesHostRef = useRef(null);

    useEffect(() => {
        // Smooth scroll must init first (installs GSAP ticker driver).
        initSmoothScroll();

        // Particles background — three.js llega en un chunk aparte
        // para no bloquear el primer render.
        let particles = null;
        let cancelled = false;
        let onScroll = null;
        if (particlesHostRef.current) {
            import("@/three/particlesScene").then(({ createParticlesScene }) => {
                if (cancelled || !particlesHostRef.current) return;
                particles = createParticlesScene(particlesHostRef.current);
                particles.setScroll(window.scrollY);
                onScroll = () => particles?.setScroll(window.scrollY);
                window.addEventListener("scroll", onScroll, { passive: true });
            });

            // Delay attaching animations one frame so DOM is ready.
            const t = setTimeout(() => {
                const cleanupAnim = initScrollAnimations();
                // Store cleanup in ref-scope
                App._cleanupAnim = cleanupAnim;
            }, 60);

            return () => {
                cancelled = true;
                clearTimeout(t);
                if (onScroll) window.removeEventListener("scroll", onScroll);
                particles?.dispose();
                destroySmoothScroll();
                if (App._cleanupAnim) {
                    App._cleanupAnim();
                    App._cleanupAnim = null;
                }
            };
        }

        return () => {
            destroySmoothScroll();
        };
    }, []);

    return (
        <LanguageProvider>
            <div className="App" data-testid="app-root">
                <div
                    ref={particlesHostRef}
                    aria-hidden="true"
                    data-testid="particles-host"
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 0,
                        pointerEvents: "none",
                    }}
                />
                <div className="ps-grain" aria-hidden="true" />

                <Nav />

                <main data-testid="main">
                    <Hero />
                    <About />
                    <Services />
                    <Tools />
                    <Marquee />
                    <Projects />
                    <Pricing />
                    <Faq />
                    <Contact />
                </main>
                <Toaster />
            </div>
        </LanguageProvider>
    );
}

export default App;
